/**
 *
 * @returns MyProfileNavigationListComponent
 *
 */
import {
  JobPostingCardType1,
  JobPostingCardType2
} from '@/components/JobPosting/JobPostingCard'
import { Block } from '@/components/base/Block'
import { useNavigator } from '@/hooks/useNavigator'
import { AppPath } from '@/routes/routes'
import { align, display } from '@/styles/display'
import { fontSize, fontWeight } from '@/styles/font'
import {
  V1JobPostingCardType,
  type V1JobPostingCardsComponent
} from 'mnvm-mypage-app-ts-if'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

export const scrollPositionKey = 'scrollPosition'

interface JobPostingCardsComponentProps {
  componentId?: string
  component: V1JobPostingCardsComponent
  isShowActionButton?: boolean
  refetch?: (scrollPosition: number) => void
  isViewMemo?: boolean
}

const JobPostingCardsComponent = ({
  component,
  isShowActionButton,
  refetch,
  isViewMemo
}: JobPostingCardsComponentProps): JSX.Element => {
  const nav = useNavigator()

  useEffect(() => {
    const scrollPosition = sessionStorage.getItem(scrollPositionKey)
    if (scrollPosition != null) {
      refetch?.(parseInt(scrollPosition, 10))

      setTimeout(() => {
        sessionStorage.removeItem(scrollPositionKey)
      }, 100)
    }
  }, [])

  const handleLinkClick = (): void => {
    sessionStorage.setItem(scrollPositionKey, window.scrollY.toString())
  }

  if (component?.jobPostingCards == null) {
    return <></>
  }

  const jobPostingCards = component?.jobPostingCards ?? []
  if (jobPostingCards.length === 0) {
    return <NoData />
  }

  return (
    <Block>
      {jobPostingCards.map((jobPostingCard, idx) => {
        const catchphrases = jobPostingCard.catchphrases ?? []
        const images = jobPostingCard.images ?? []

        switch (component?.cardType) {
          case V1JobPostingCardType.Plane:
            return (
              <Link
                key={`JobPostingCardType1_${idx}`}
                to={AppPath.JobPostingDetail.replace(
                  ':id',
                  jobPostingCard?.id?.toString() ?? ''
                )}
                style={{ textDecoration: 'none' }}
              >
                <JobPostingCardType1
                  labels={jobPostingCard.labels ?? []}
                  tags={jobPostingCard.tags ?? []}
                  attachmentInfo={jobPostingCard.attachmentInfo ?? []}
                  title={jobPostingCard.title ?? ''}
                  smallTitle={jobPostingCard.body ?? ''}
                  companyName={jobPostingCard.companyName ?? ''}
                  code={jobPostingCard.uuid ?? ''}
                  isLiked={jobPostingCard.isLiked}
                />
              </Link>
            )
          case V1JobPostingCardType.Rich:
            return (
              <JobPostingCardType2
                key={`JobPostingCardType2_${idx}`}
                jobPostingId={jobPostingCard.id}
                labels={jobPostingCard.labels ?? []}
                imageUrl={images.length > 0 ? images[0] : ''}
                tags={jobPostingCard.tags ?? []}
                attachmentInfo={jobPostingCard.attachmentInfo ?? []}
                title={jobPostingCard.title ?? ''}
                smallTitle={jobPostingCard.body ?? ''}
                shopName={jobPostingCard.shopName ?? ''}
                recommendComment={
                  catchphrases.length > 0 ? catchphrases[0].text ?? '' : ''
                }
                code={jobPostingCard.uuid ?? ''}
                update={jobPostingCard.srcUpdateDate ?? ''}
                isShowActionButton={isShowActionButton ?? false}
                isViewMemo={isViewMemo}
                isAcceptedOffer={jobPostingCard.isAcceptedOffer ?? false}
                isDeclinedApplication={
                  jobPostingCard.isDeclinedApplication ?? false
                }
                onClickShowDetailButton={() => {
                  handleLinkClick()
                  void nav?.gotoJobPostingDetail({
                    id: jobPostingCard.id,
                    fromList: true
                  })
                }}
                isLiked={jobPostingCard.isLiked}
                isRecommended={jobPostingCard.isRecommended}
                showDetailView={jobPostingCard.showDetailView ?? false}
                advisorComment={jobPostingCard.advisorComment}
                introductionDate={jobPostingCard.introductionDate ?? ''}
                isConsidering={jobPostingCard.isConsidering ?? false}
                mpOrderMemo={jobPostingCard.mpOrderMemo ?? ''}
              />
            )

          default:
            return <></>
        }
      })}
    </Block>
  )
}

const NoData = (): JSX.Element => {
  return (
    <Block
      height="calc(100vh - 250px)"
      justifyContent={align.CENTER}
      alignItems={align.CENTER}
      display={display.FLEX}
      fontSize={fontSize.M}
      fontWeight={fontWeight.Regular}
    >
      対象の求人がありません。
    </Block>
  )
}

export default JobPostingCardsComponent
